/**
 * The contract of the food suggestion route, `POST /api/food-suggest` (#5410).
 *
 * The app sends what the person has typed so far; the server answers with whole search lines
 * ("chicken breast", "chicken breasts"), each with the number of foods whose name holds those
 * words, most foods first. See `design-docs/food-search-suggestions.md`.
 */

/** The longest text the route accepts. Longer text is refused, never cut short. */
export const FOOD_SUGGEST_TEXT_MAX_LENGTH = 64;

/** The most suggestions one request may ask for — an abuse limit; the screen shows fewer. */
export const FOOD_SUGGEST_SIZE_MAX = 10;

/**
 * The fewest foods a phrase must appear in to be in the suggestion index at all. A phrase the
 * index does not hold is therefore in FEWER than this many foods, which is what lets the app put
 * a number on "how common is what you typed" even when it finds no row for it.
 *
 * MIRRORED VALUE — the build applies this cut (`--cut`, defaulted in
 * `tools/src/food-suggest-build.ts`) and this constant only describes it. Change the build's
 * default and this must change with it; nothing checks that they agree. The reasoning behind the
 * cut is in `design-docs/food-search-suggestions.md`.
 */
export const FOOD_SUGGEST_MIN_PHRASE_COUNT = 5;

/** The request body. Exactly these two fields; anything else is refused. */
export interface FoodSuggestRequest {
  /** what the person has typed, as typed */
  text: string;
  /** how many suggestions to answer with, 1 to `FOOD_SUGGEST_SIZE_MAX` */
  size: number;
}

export interface FoodSuggestion {
  /** the search line to offer, with its accents as the food names spell it */
  phrase: string;
  /** how many foods have these words, in this order, in their name or one of their other names */
  count: number;
  /**
   * True when a letter had to be changed to reach this phrase from what was typed — so it is a
   * correction, not a continuation. Absent means the phrase matches what was typed exactly.
   *
   * The app uses it to decide whether a committed search was probably a misspelling, and whether
   * to offer the correction or run it instead. See `design-docs/food-search-suggestions.md`,
   * "Correcting a committed search".
   */
  mended?: boolean;
}

export interface FoodSuggestResponse {
  /** most foods first; empty when nothing matches or the text holds no word */
  suggestions: FoodSuggestion[];
}

/**
 * The `error` the route answers with its 504 when Elasticsearch missed the route's own deadline.
 * The client checks for it so that only THIS 504 goes unreported (#6130): a 504 from a gateway or
 * proxy in front of the server carries no such body and is still a failure worth knowing about.
 */
export const FOOD_SUGGEST_DEADLINE_ERROR = 'Food suggestions timed out';
